import React, { useState, useEffect } from "react";
import { Card, Form, Button, Alert, Spinner, Row, Col } from "react-bootstrap";
import Cookies from "js-cookie";
import axios from "axios";
import { Shop, Bell, Palette, Gear } from "react-bootstrap-icons";
import { API_BASE_URL as API_BASE } from "../../../config";

function ConfiguracoesApi() {
  const [appKey, setAppKey] = useState("");
  const [authKey, setAuthKey] = useState("");
  const [tempoLembrete, setTempoLembrete] = useState(0);
  const [atualizacaoAutomatica, setAtualizacaoAutomatica] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Cores
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [textColor, setTextColor] = useState("");
  const [buttonColor, setButtonColor] = useState("");
  const [buttonTextColor, setButtonTextColor] = useState("");

  // Dados do ClienteMaster
  const [nome, setNome] = useState("");
  const [celular, setCelular] = useState("");
  const [slug, setSlug] = useState("");

  // Sempre ativo
  const ativo = true;

  // Pega o usuário do cookie de forma segura
  function getUsuarioCookieSafe() {
    try {
      const raw = Cookies.get("usuario");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  // Verifica se slug já existe
  const checkSlugExists = async (slugToCheck) => {
    try {
      const token = Cookies.get("authToken");
      const response = await axios.get(`${API_BASE}/ClienteMaster/check-slug/${slugToCheck}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.exists;
    } catch (err) {
      console.error("Erro ao verificar slug:", err);
      return false;
    }
  };

  // Carrega as configurações do cliente master
  useEffect(() => {
    const fetchConfig = async () => {
      const usuario = getUsuarioCookieSafe();
      if (!usuario || usuario.role !== "ClienteMaster") {
        setError("Acesso negado. Você não é um cliente master.");
        setLoading(false);
        return;
      }

      try {
        const token = Cookies.get("authToken");
        const response = await axios.get(`${API_BASE}/ClienteMaster/${usuario.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response.data || {};

        // API de lembretes sempre ativa
        setTempoLembrete(data.tempoLembrete ?? 0);

        setAppKey(data.appKey || "");
        setAuthKey(data.authKey || "");
        setPrimaryColor(data.primaryColor || "");
        setSecondaryColor(data.secondaryColor || "");
        setTextColor(data.textColor || "");
        setButtonColor(data.buttonColor || "");
        setButtonTextColor(data.buttonTextColor || "");
        setNome(data.nome || "");
        setCelular(data.celular || "");
        setSlug(data.slug || "");
        setAtualizacaoAutomatica(!!data.atualizacaoAutomatica);
      } catch (err) {
        console.error("Erro ao buscar configurações:", err);
        setError("Erro ao carregar configurações.");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Salvar configurações
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    const usuario = getUsuarioCookieSafe();
    if (!usuario || usuario.role !== "ClienteMaster") {
      setError("Acesso negado.");
      setSaving(false);
      return;
    }

    // Verifica slug duplicado
    if (slug) {
      const slugExists = await checkSlugExists(slug);
      if (slugExists && slug !== usuario.slug) {
        setError("Este slug já está em uso por outra empresa.");
        setSaving(false);
        return;
      }
    }

    try {
      const token = Cookies.get("authToken");
      await axios.put(
        `${API_BASE}/ClienteMaster/${usuario.id}`,
        {
          usaApiLembrete: true, // sempre true
          appKey,
          authKey,
          tempoLembrete,
          primaryColor,
          secondaryColor,
          textColor,
          buttonColor,
          buttonTextColor,
          nome,
          celular,
          slug,
          atualizacaoAutomatica,
          ativo,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
    } catch (err) {
      console.error("Erro ao salvar configurações:", err);
      setError("Erro ao salvar configurações.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner animation="border" />;

  const cardStyle = {
    backgroundColor: "#3c4049",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "25px",
    width: "95%",
    maxWidth: "1200px",
    margin: "0 auto",
  };

  return (
    <Card className="shadow-lg" style={cardStyle}>
      <Card.Title className="mb-4 fs-3 text-center">
        <Gear size={28} className="me-2" />
        Configurações do Sistema
      </Card.Title>

      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">Configurações salvas com sucesso!</Alert>}

        <Form onSubmit={handleSubmit}>
          {/* Dados principais */}
          <h4 className="mb-3"><Shop className="me-2" size={22} />Dados da Empresa</h4>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group controlId="formNome" className="mb-3">
                <Form.Label>Nome</Form.Label>
                <Form.Control
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formTelefone" className="mb-3">
                <Form.Label>Telefone / WhatsApp</Form.Label>
                <Form.Control
                  type="text"
                  value={celular}
                  onChange={(e) => setCelular(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="formSlug" className="mb-3">
                <Form.Label>Slug (URL pública)</Form.Label>
                <Form.Control
                  type="text"
                  value={slug}
                  onChange={(e) =>
                    setSlug(e.target.value.replace(/\s+/g, "-").toLowerCase())
                  }
                />
                <Form.Text className="text-light">
                  Ex.: <code>https://seusite.com/{slug || "minha-empresa"}</code>
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* Configurações Gerais */}
          <h4 className="mb-3"><Gear className="me-2" size={22} />Configurações Gerais</h4>
          <Form.Group className="mb-3" controlId="formAtualizacaoAutomatica">
            <Form.Check
              type="switch"
              label="Concluir automaticamente os agendamentos passados ao fazer login"
              checked={atualizacaoAutomatica}
              onChange={(e) => setAtualizacaoAutomatica(e.target.checked)}
            />
            <Form.Text className="text-light">
              Se ativado, sempre que você ou um funcionário fizer login, o sistema marcará como 'Concluído' todos os agendamentos pendentes que já passaram.
            </Form.Text>
          </Form.Group>

          <hr className="my-4" />

          {/* API de Lembretes */}
          <h4 className="mb-3"><Bell className="me-2" size={22} />API de Lembretes</h4>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group controlId="formTempoLembrete" className="mb-3">
                <Form.Label>Tempo Lembrete (min antes)</Form.Label>
                <Form.Control
                  type="number"
                  value={tempoLembrete}
                  onChange={(e) => setTempoLembrete(parseInt(e.target.value, 10) || 0)}
                  required
                />
                <Form.Text className="text-light">
                  Defina quantos minutos antes o usuário deve receber o lembrete.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-4" />

          <div className="d-flex justify-content-end mt-4">
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Salvar Todas as Configurações"}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

export default ConfiguracoesApi;
